import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, BookOpen, Link2, Unlink, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { chf, periodeLabel, STATUT_DETTE } from "./salairesUtils";

export default function SalairesComptaTab() {
  const [periodes, setPeriodes] = useState([]);
  const [rappro, setRappro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filtrePeriode, setFiltrePeriode] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/salaires/periodes"),
        api.get("/salaires/rapprochement", { params: filtrePeriode ? { periode: filtrePeriode } : {} }),
      ]);
      setPeriodes(p.data.items || []);
      setRappro(r.data);
    } finally { setLoading(false); }
  }, [filtrePeriode]);

  useEffect(() => { fetch(); }, [fetch]);

  const comptabiliser = async (periode) => {
    setBusy(periode);
    try {
      const { data } = await api.post(`/salaires/periodes/${periode}/comptabiliser`, {});
      toast.success(`${data.nb_fiches} fiche(s) comptabilisée(s) · ${data.nb_ecritures} écritures créées`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur", { duration: 7000 }); }
    finally { setBusy(null); }
  };

  const decomptabiliser = async (periode) => {
    setBusy(periode);
    try {
      const { data } = await api.post(`/salaires/periodes/${periode}/decomptabiliser`);
      toast.success(`${data.nb_extournes} écriture(s) extournée(s)`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur", { duration: 7000 }); }
    finally { setBusy(null); }
  };

  const rapprocher = async (dette, cand) => {
    setBusy(dette.id);
    try {
      const body = cand.source === "camt"
        ? { provisoire_id: cand.id, montant: cand.montant }
        : { ecriture_id: cand.id, montant: cand.montant };
      const { data } = await api.post(`/salaires/dettes/${dette.id}/rapprocher`, body);
      toast.success(cand.source === "camt"
        ? `Paiement rapproché · écriture de paiement créée (reste ${chf(data.reste)})`
        : `Écriture existante rattachée — aucune écriture dupliquée (reste ${chf(data.reste)})`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur", { duration: 7000 }); }
    finally { setBusy(null); }
  };

  const derapprocher = async (dette) => {
    setBusy(dette.id);
    try {
      await api.post(`/salaires/dettes/${dette.id}/derapprocher`, {});
      toast.success("Rapprochement annulé");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="salaires-compta">
      <Card className="border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-heading font-semibold text-gray-900">Périodes salariales</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            La comptabilisation crée les écritures en partie double dans le journal comptable.
            Une fiche déjà comptabilisée est ignorée : aucune écriture en double.
          </p>
        </div>
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : periodes.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">Aucune période calculée</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Période</th>
                  <th className="px-3 py-2">Fiches</th>
                  <th className="px-3 py-2 text-right">Brut</th>
                  <th className="px-3 py-2 text-right">Net à payer</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="periodes-table">
                {periodes.map((p) => (
                  <tr key={p.periode} className="border-t border-gray-100">
                    <td className="px-3 py-2.5 font-semibold">{periodeLabel(p.periode)}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {p.brouillon ? <span className="mr-2">{p.brouillon} brouillon</span> : null}
                      {p.validee ? <span className="mr-2 text-blue-700">{p.validee} validée(s)</span> : null}
                      {p.comptabilisee ? <span className="text-emerald-700">{p.comptabilisee} comptabilisée(s)</span> : null}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{chf(p.brut)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{chf(p.net)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {p.validee > 0 && (
                        <Button size="sm" onClick={() => comptabiliser(p.periode)} disabled={busy === p.periode}
                                className="bg-[#52a675] hover:bg-[#3d8a5c] mr-2" data-testid={`comptabiliser-${p.periode}`}>
                          {busy === p.periode ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4 mr-1.5" />}
                          Comptabiliser
                        </Button>
                      )}
                      {p.comptabilisee > 0 && (
                        <Button size="sm" variant="outline" onClick={() => decomptabiliser(p.periode)}
                                disabled={busy === p.periode} data-testid={`decomptabiliser-${p.periode}`}>
                          Extourner
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-end gap-3">
          <div>
            <h3 className="font-heading font-semibold text-gray-900">Paiements et rapprochement</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Salaires nets et charges sociales à payer, rapprochés avec les mouvements bancaires
              CAMT ou les écritures déjà passées (fiduciaire).
            </p>
          </div>
          <div className="ml-auto">
            <Label className="text-xs">Filtrer une période</Label>
            <Input type="month" value={filtrePeriode} onChange={(e) => setFiltrePeriode(e.target.value)}
                   className="mt-1 w-40" data-testid="rappro-filtre-periode" />
          </div>
        </div>

        <div className="p-4 space-y-3">
          {rappro && (
            <div className="text-xs text-gray-500">
              {rappro.nb_mouvements_camt} mouvement(s) bancaire(s) en attente ·
              {" "}{rappro.nb_ecritures_candidates} écriture(s) du journal analysée(s)
            </div>
          )}
          {(rappro?.dettes || []).length === 0 ? (
            <p className="text-sm text-gray-400">Aucune dette salariale ouverte</p>
          ) : (
            rappro.dettes.map((d) => {
              const st = STATUT_DETTE[d.statut] || STATUT_DETTE.ouverte;
              return (
                <div key={d.id} className="border border-gray-200 rounded-lg p-3" data-testid={`dette-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                    <span className="font-semibold text-gray-900">{d.libelle}</span>
                    <span className="text-xs text-gray-500">compte {d.compte}</span>
                    <span className="ml-auto font-mono font-bold">{chf(d.montant)}</span>
                    {d.montant_rapproche > 0 && (
                      <span className="text-xs text-gray-500">rapproché {chf(d.montant_rapproche)} · reste {chf(d.reste)}</span>
                    )}
                    {(d.liens || []).length > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => derapprocher(d)} disabled={busy === d.id}
                              title="Annuler le rapprochement" data-testid={`derapprocher-${d.id}`}>
                        <Unlink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {(d.candidats || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {d.candidats.map((cd) => (
                        <div key={`${cd.source}-${cd.id}`}
                             className="flex flex-wrap items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded px-2 py-1.5">
                          <Badge variant="outline" className={cd.source === "camt"
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : "bg-purple-50 text-purple-700 border-purple-200"}>
                            {cd.source === "camt" ? "Banque (CAMT)" : "Écriture existante"}
                          </Badge>
                          <span className="text-gray-600">{cd.date}</span>
                          <span className="font-mono">{chf(cd.montant)}</span>
                          <span className="text-gray-500 truncate max-w-[280px]">{cd.libelle}</span>
                          {!cd.certain && (
                            <span className="flex items-center gap-1 text-amber-700">
                              <AlertTriangle className="w-3 h-3" /> à confirmer
                            </span>
                          )}
                          <Button size="sm" variant="outline" className="ml-auto h-7"
                                  onClick={() => rapprocher(d, cd)} disabled={busy === d.id}
                                  data-testid={`rapprocher-${d.id}-${cd.id}`}>
                            <Link2 className="w-3.5 h-3.5 mr-1" /> Rapprocher
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {(d.liens || []).map((l, i) => (
                    <div key={i} className="mt-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                      Rapproché le {String(l.rapproche_at || "").slice(0, 10)} · {chf(l.montant)} ·
                      {l.cree_ecriture ? " écriture de paiement créée" : " écriture existante rattachée (aucun doublon)"}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
}
